// PeptideVault atomic sync safety helpers.
// Pure functions live here so merge and regression behavior can be tested
// without a browser, Electron, or Cloudflare runtime.

const ATOMIC_SYNC_FORMAT = 'peptide-vault-atomic-v2'
const ATOMIC_SYNC_KEY = 'sync_snapshot_v2'
const ATOMIC_MANIFEST_FORMAT = 'peptide-vault-manifest-v2'

function clone(value) {
  if (value === undefined) return undefined
  if (value === null) return null
  return JSON.parse(JSON.stringify(value))
}

function recordKey(record, index) {
  if (record && typeof record === 'object') {
    if (record.id != null) return 'id:' + record.id
    if (record.label != null) return 'label:' + record.label
    if (record.name != null) return 'name:' + record.name
  }
  return 'value:' + JSON.stringify(record) + ':' + index
}

function recordMap(records) {
  var map = {}
  ;(records || []).forEach(function(record, index) { map[recordKey(record, index)] = record })
  return map
}

function same(left, right) {
  return JSON.stringify(left) === JSON.stringify(right)
}

function isoTime(value) {
  var parsed = Date.parse(value || 0)
  return Number.isFinite(parsed) ? parsed : 0
}

function newerImportedRecord(local, remote) {
  var localImport = isoTime(local && local.sourceImportedAt)
  var remoteImport = isoTime(remote && remote.sourceImportedAt)
  if (!localImport && !remoteImport) return null
  if (localImport === remoteImport) return null
  return localImport > remoteImport ? local : remote
}

function mergeChangedRecord(base, local, remote, localChangedAt, remoteChangedAt) {
  var importedWinner = newerImportedRecord(local, remote)
  if (importedWinner) return clone(importedWinner)
  var merged = Object.assign({}, remote)
  // Prefer record edit time when available. A stale device reconnecting today
  // must not make an older offline edit look newer merely because it synced now.
  var localRecordAt = local && (local.updatedAt || local.createdAt) || localChangedAt
  var remoteRecordAt = remote && (remote.updatedAt || remote.createdAt) || remoteChangedAt
  var localWinsTie = isoTime(localRecordAt) >= isoTime(remoteRecordAt)
  Object.keys(local || {}).forEach(function(field) {
    var baseValue = base ? base[field] : undefined
    var localChanged = !same(local[field], baseValue)
    var remoteChanged = !same(remote && remote[field], baseValue)
    if (localChanged && (!remoteChanged || localWinsTie)) merged[field] = clone(local[field])
  })
  return merged
}

function mergeDomain(baseRecords, localRecords, remoteRecords, localChangedAt, remoteChangedAt, localTombstones, remoteTombstones) {
  var base = recordMap(baseRecords), local = recordMap(localRecords), remote = recordMap(remoteRecords)
  var tombstones = Object.assign({}, remoteTombstones || {})
  Object.keys(localTombstones || {}).forEach(function(key) {
    if (isoTime(localTombstones[key]) >= isoTime(tombstones[key])) tombstones[key] = localTombstones[key]
  })
  var keys = new Set(Object.keys(base).concat(Object.keys(local), Object.keys(remote), Object.keys(tombstones)))
  var result = []
  keys.forEach(function(key) {
    var b = base[key], l = local[key], r = remote[key]
    var localChanged = !same(l, b), remoteChanged = !same(r, b)
    var localDeleted = l === undefined && !!(localTombstones && localTombstones[key])
    var remoteDeleted = r === undefined && !!(remoteTombstones && remoteTombstones[key])
    var localDeleteAt = localTombstones && localTombstones[key]
    var remoteDeleteAt = remoteTombstones && remoteTombstones[key]
    var localRecordAt = l && (l.updatedAt || l.createdAt) || localChangedAt
    var remoteRecordAt = r && (r.updatedAt || r.createdAt) || remoteChangedAt

    // Explicit tombstones prevent an unchanged stale device from resurrecting
    // a record that another device intentionally deleted.
    if (localDeleted && (!remoteChanged || isoTime(localDeleteAt || localChangedAt) >= isoTime(remoteRecordAt))) return
    if (remoteDeleted && (!localChanged || isoTime(remoteDeleteAt || remoteChangedAt) > isoTime(localRecordAt))) return
    if (l === undefined && r === undefined) return
    // Absence without a deletion record is not authority to remove a record,
    // including when it existed in the last shared baseline.
    if (l === undefined && !localDeleted) { result.push(clone(r)); return }
    if (r === undefined && !remoteDeleted) { result.push(clone(l)); return }
    if (!localChanged) { if (r !== undefined) result.push(clone(r)); return }
    if (!remoteChanged) { if (l !== undefined) result.push(clone(l)); return }
    if (l === undefined || r === undefined) {
      var survivor = isoTime(localRecordAt) >= isoTime(remoteRecordAt) ? l : r
      if (survivor !== undefined) result.push(clone(survivor))
      return
    }
    result.push(mergeChangedRecord(b, l, r, localChangedAt, remoteChangedAt))
  })
  return result
}

function mergeSettings(baseSettings, localSettings, remoteSettings, localChangedAt, remoteChangedAt) {
  var base = baseSettings || {}, local = localSettings || {}, remote = remoteSettings || {}
  var merged = clone(remote)
  var localWinsTie = isoTime(localChangedAt) >= isoTime(remoteChangedAt)
  Object.keys(local).forEach(function(field) {
    var localChanged = !same(local[field], base[field])
    var remoteChanged = !same(remote[field], base[field])
    if (localChanged && (!remoteChanged || localWinsTie)) merged[field] = clone(local[field])
  })
  return merged
}

function mergeAtomicSnapshots(baseSnapshot, localSnapshot, remoteSnapshot, domainKeys, localChangedAt, remoteChangedAt) {
  var baseDomains = baseSnapshot && baseSnapshot.domains || {}
  var localDomains = localSnapshot && localSnapshot.domains || {}
  var remoteDomains = remoteSnapshot && remoteSnapshot.domains || {}
  var localTombstones = localSnapshot && localSnapshot.tombstones || {}
  var remoteTombstones = remoteSnapshot && remoteSnapshot.tombstones || {}
  var domains = {}
  ;(domainKeys || []).forEach(function(key) {
    domains[key] = mergeDomain(baseDomains[key] || [], localDomains[key] || [], remoteDomains[key] || [], localChangedAt, remoteChangedAt, localTombstones[key], remoteTombstones[key])
  })
  var mergedTombstones = clone(remoteTombstones)
  Object.keys(localTombstones).forEach(function(domain) {
    if (!mergedTombstones[domain]) mergedTombstones[domain] = {}
    Object.keys(localTombstones[domain] || {}).forEach(function(key) {
      if (isoTime(localTombstones[domain][key]) >= isoTime(mergedTombstones[domain][key])) mergedTombstones[domain][key] = localTombstones[domain][key]
    })
  })
  return {
    format: ATOMIC_SYNC_FORMAT,
    schemaVersion: Math.max(Number(localSnapshot && localSnapshot.schemaVersion || 0), Number(remoteSnapshot && remoteSnapshot.schemaVersion || 0)),
    domains: domains,
    settingsData: mergeSettings(baseSnapshot && baseSnapshot.settingsData, localSnapshot && localSnapshot.settingsData, remoteSnapshot && remoteSnapshot.settingsData, localChangedAt, remoteChangedAt),
    tombstones: mergedTombstones
  }
}

function atomicSnapshotSummary(currentDomains, incomingDomains, domainKeys) {
  var summary = { currentRecords:0, incomingRecords:0, added:0, removed:0, modified:0, domains:{} }
  ;(domainKeys || []).forEach(function(key) {
    var current = recordMap(currentDomains[key] || []), incoming = recordMap(incomingDomains[key] || [])
    var item = { current:(currentDomains[key] || []).length, incoming:(incomingDomains[key] || []).length, added:0, removed:0, modified:0 }
    Object.keys(incoming).forEach(function(id) { if (!(id in current)) item.added++; else if (!same(current[id], incoming[id])) item.modified++ })
    Object.keys(current).forEach(function(id) { if (!(id in incoming)) item.removed++ })
    summary.currentRecords += item.current
    summary.incomingRecords += item.incoming
    summary.added += item.added; summary.removed += item.removed; summary.modified += item.modified
    if (item.added || item.removed || item.modified) summary.domains[key] = item
  })
  return summary
}

function validateAtomicSnapshot(snapshot, currentDomains, domainKeys) {
  var errors = [], warnings = []
  if (!snapshot || snapshot.format !== ATOMIC_SYNC_FORMAT) errors.push('Unsupported cloud snapshot format')
  if (!snapshot || !snapshot.domains || typeof snapshot.domains !== 'object') errors.push('Snapshot domains are missing')
  ;(domainKeys || []).forEach(function(key) {
    if (!snapshot || !Array.isArray(snapshot.domains && snapshot.domains[key])) errors.push(key + ' is not a valid record list')
  })
  if (errors.length) return { ok:false, errors:errors, warnings:warnings, summary:null }
  var summary = atomicSnapshotSummary(currentDomains || {}, snapshot.domains, domainKeys)
  if (summary.currentRecords > 0 && summary.incomingRecords < summary.currentRecords * 0.7) warnings.push('Cloud snapshot contains over 30% fewer records')

  var protectedDomains = new Set(['peptides','documents','storageLocations','supplements','protocols','inventoryReservations','coas','vaultPositions','vaultAssignments'])
  protectedDomains.forEach(function(domain) {
    if (!(domain in (currentDomains || {}))) return
    var current = recordMap(currentDomains[domain] || []), incoming = recordMap(snapshot.domains[domain] || [])
    Object.keys(current).forEach(function(key) {
      if (!(key in incoming) && !(snapshot.tombstones && snapshot.tombstones[domain] && snapshot.tombstones[domain][key])) {
        errors.push('Cloud removal has no deletion record: ' + domain + ' ' + key)
      }
    })
  })

  // A source import is authoritative as a complete schedule transaction. Never
  // silently replace it with an older import of the same episode.
  var currentProtocols = recordMap((currentDomains && currentDomains.protocols) || [])
  ;(snapshot.domains.protocols || []).forEach(function(protocol, index) {
    var prior = currentProtocols[recordKey(protocol, index)]
    if (!prior) return
    if (isoTime(prior.sourceImportedAt) > isoTime(protocol.sourceImportedAt)) {
      errors.push('Cloud would roll back newer imported protocol: ' + (prior.name || prior.peptideName || prior.id))
    }
  })
  return { ok:errors.length === 0, errors:errors, warnings:warnings, summary:summary }
}

export {
  ATOMIC_SYNC_FORMAT,
  ATOMIC_SYNC_KEY,
  ATOMIC_MANIFEST_FORMAT,
  recordKey,
  mergeDomain,
  mergeAtomicSnapshots,
  atomicSnapshotSummary,
  validateAtomicSnapshot
}
